import { handleIncomingPush } from "../firebase/handleIncomingPush";
import { registerProvidedToken } from "../firebase/fcmService";
import { navigateTo } from "./navigationRef";
import store from "../../dataManagement/uiDb/UiDb";
import { setNativePermission } from "../../dataManagement/uiDb/NotificationsDb";
import { getNativeFcmToken, toNativeNotificationState } from "./androidBridge";
import { deliverNativeMicState } from "./micPermission";
import { bridgeLog } from "./bridgeLog";

// The web side of the Android bridge contract. The native app calls these via
// evaluateJavascript:
//   window.NineFindsNative.registerFcmToken(token)
//   window.NineFindsNative.onPushMessage(dataJson)          // foreground push
//   window.NineFindsNative.navigate(path)                   // deep-link routing
//   window.NineFindsNative.onPermissionChanged(name, state) // permission updates
interface NineFindsNativeApi {
  registerFcmToken: (token: string) => void;
  onPushMessage: (dataJson: string) => void;
  navigate: (path: string) => void;
  onPermissionChanged: (name: string, state: string) => void;
}

declare global {
  interface Window {
    NineFindsNative?: NineFindsNativeApi;
  }
}

/**
 * Pulls the native FCM token and registers it with the backend, reconciling
 * against the user's server-side token list (force-register when missing). Used
 * on login and again when permission flips to granted.
 */
export function pullAndRegisterNativeToken(): void {
  const token = getNativeFcmToken();
  if (!token) {
    bridgeLog("getFcmToken() returned empty");
    return;
  }
  const tokens = store.getState().profile.data?.fcmTokens;
  const serverTokens: string[] = Array.isArray(tokens) ? tokens : [];
  if (serverTokens.includes(token)) {
    bridgeLog("FCM token already registered — skipping");
  } else {
    registerProvidedToken(token, true);
    bridgeLog("registering FCM token (POST)");
  }
}

/** Installs window.NineFindsNative once. Safe to call on any platform. */
export function installNativeBridge(): void {
  window.NineFindsNative = {
    registerFcmToken: (token: string) => {
      if (!token) return;
      const tokens = store.getState().profile.data?.fcmTokens;
      const serverTokens: string[] = Array.isArray(tokens) ? tokens : [];
      if (serverTokens.includes(token)) {
        bridgeLog("FCM token already registered — skipping");
        return;
      }
      registerProvidedToken(token, true);
    },
    onPushMessage: (dataJson: string) => {
      try {
        const data =
          typeof dataJson === "string" ? JSON.parse(dataJson) : dataJson;
        handleIncomingPush({ data });
      } catch (error) {
        console.warn("NineFindsNative.onPushMessage parse failed:", error);
      }
    },
    navigate: (path: string) => {
      // Fall back to the inbox when no deep link is provided.
      navigateTo(path || "/my-messages");
    },
    onPermissionChanged: (name: string, state: string) => {
      // Fired after the OS dialog is answered AND when the user returns from the
      // system Settings screen.
      if (name === "microphone") {

        deliverNativeMicState(state);
        return;
      }
      if (name !== "notifications") return;
      const normalized = toNativeNotificationState(state);
      store.dispatch(setNativePermission(normalized));
      // Just enabled — register this device's token without a reload.
      if (normalized === "granted") pullAndRegisterNativeToken();
    },
  };
}
